import React, { Component } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { animateScroll } from 'react-scroll';
import { anonymise } from 'utils/data';
import ConfirmationField from 'components/ConfirmationField';
import endpoints from 'utils/endpoints';
import i18n from 'utils/translate';
import { formatPrice, isAustrianIBAN } from 'utils/common';
import { TRAVEL_FILTERS, PAYMENT } from 'utils/constants';
import Section from 'components/Section';
import loader from '../../../../../images/loader-big.gif';
import NavigationButtons from 'components/NavigationButtons';
import './styles.scss';
import { linkTypeShape } from 'utils/types';
import { countries } from 'utils/constants';

const propTypes = {
  paymentPending: PropTypes.bool,
  backLink: PropTypes.shape(linkTypeShape),
  info: PropTypes.shape({
    vacation: PropTypes.shape({
      type: PropTypes.oneOf([TRAVEL_FILTERS.SINGLE, TRAVEL_FILTERS.FAMILY]).isRequired,
      destination: PropTypes.oneOf([TRAVEL_FILTERS.INLAND, TRAVEL_FILTERS.FOREIGN]).isRequired,
      startDate: PropTypes.object.isRequired,
      endDate: PropTypes.object.isRequired,
      cancellation: PropTypes.bool
    }),
    personal: PropTypes.shape({
      anrede: PropTypes.number,
      vorname: PropTypes.string.isRequired,
      nachname: PropTypes.string.isRequired,
      geburtsdatum: PropTypes.any.isRequired,
      strasse: PropTypes.string.isRequired,
      nr: PropTypes.any.isRequired,
      plz: PropTypes.any.isRequired,
      ort: PropTypes.string.isRequired,
      land: PropTypes.string.isRequired,
      email: PropTypes.string.isRequired,
      telefon: PropTypes.string.isRequired
    }).isRequired,
    coinsured: PropTypes.any,
    brokerNumber: PropTypes.string,
    payment: PropTypes.shape({
      type: PropTypes.oneOf([PAYMENT.HOBEX, PAYMENT.SEPA]).isRequired,
      iban: PropTypes.string,
      bic: PropTypes.string
    }).isRequired,
    promotion: PropTypes.bool,
    visaRequired: PropTypes.bool,
    cubaTrip: PropTypes.bool,
    finalPrice: PropTypes.number,
    currency: PropTypes.string
  }),
  onConfirm: PropTypes.func.isRequired,
  apiErrorMessages: PropTypes.array
};
const defaultProps = {};
const insuranceName = 'GRAWE TRAVELSTAR';

class Confirmation extends Component {
  componentDidMount() {
    animateScroll.scrollToTop({ duration: 0 });
  }

  state = {
    blockConfirmClick: false
  };

  onClick = () => {
    const { blockConfirmClick } = this.state;

    if (!blockConfirmClick) {
      const { onConfirm, info } = this.props;
      const prefix = window.base === '/' ? '' : window.base;

      let requestInfo = {
        endpoint: '',
        request: {
          vermittlerNr: info.brokerNumber,
          reisebeginn: moment(info.vacation.startDate).format('DD.MM.YYYY'),
          reiseende: moment(info.vacation.endDate).format('DD.MM.YYYY'),
          buchungsdatum:
            info.vacation.cancellation === true ? moment(info.vacation.bookingDate).format('DD.MM.YYYY') : null,
          versicherungstyp: info.vacation.type,
          stornoschutz: info.vacation.cancellation,
          kubaReise: info.cubaTrip,
          visum: info.visaRequired,
          graweInfos: info.promotion,
          bezahlung: {
            type: info.payment.type
          },
          versicherter: {
            anrede: info.personal.anrede,
            vorname: info.personal.vorname,
            nachname: info.personal.nachname,
            geburtsdatum: moment(info.personal.geburtsdatum).format('DD.MM.YYYY'),
            strasse: info.personal.strasse,
            nr: info.personal.nr,
            plz: info.personal.plz,
            ort: info.personal.ort,
            land: info.personal.land,
            email: info.personal.email,
            telefon: info.personal.telefon
          },
          mitversicherte: [],
          mailversand: info.acceptEmailSend
        }
      };

      if (info.payment.type === PAYMENT.SEPA) {
        requestInfo.endpoint = endpoints.travel.SEPA;
        requestInfo.request.bezahlung.iban = info.payment.iban.replace(/\s+/g, '');
        requestInfo.request.bezahlung.bic = this.getBIC(info.payment.iban, info.payment.bic);
        requestInfo.request.bezahlung.paymentMethod = 'SEPA_DIRECTDEBITMANDATE';
        requestInfo.request.bezahlung.type = PAYMENT.SEPA;
      } else {
        requestInfo.endpoint = endpoints.travel.HOBEX_PAYMENT;
        requestInfo.request.bezahlung.hobexRequestUrl = `${window.location.origin}${prefix}/confirmation`;
        requestInfo.request.bezahlung.paymentMethod = 'CREDITCARD';
        requestInfo.request.bezahlung.type = PAYMENT.HOBEX;
      }

      if (info.vacation.type === TRAVEL_FILTERS.FAMILY) {
        info.coinsured.forEach(person => {
          let newPerson = {};
          newPerson.anrede = person.anrede;
          newPerson.vorname = person.vorname;
          newPerson.nachname = person.nachname;
          newPerson.geburtsdatum = person.geburtsdatum.format('DD.MM.YYYY');

          requestInfo.request.mitversicherte.push(newPerson);
        });
      }

      this.setState({ blockConfirmClick: true }, () => {
        onConfirm(requestInfo);
        window.dataLayer.push({
          event: 'onlineabschluss',
          formname: 'Reiseversicherung',
          action: 'Schritt 4 von 4'
        });
      });
    }
  };

  getInsuranceType = type =>
    type === TRAVEL_FILTERS.FAMILY ? i18n('Travelstar Family & Friends') : i18n('Travelstar Single');

  getDestinationType = destination => (destination === TRAVEL_FILTERS.FOREIGN ? i18n('Ausland') : i18n('Inland'));

  getDateString = () => {
    const {
      info: { vacation }
    } = this.props;

    return `${moment(vacation.startDate).format('DD.MM.YYYY')}
            ${i18n('bis')} ${moment(vacation.endDate).format('DD.MM.YYYY')}`;
  };

  getNameString = () => {
    const {
      info: { personal }
    } = this.props;

    if (!personal.vorname || !personal.nachname) return '';

    return `${personal.anrede ? i18n('Frau') : i18n('Herr')} ${personal.vorname} ${personal.nachname}`;
  };

  getPaymentType = type => (type === PAYMENT.HOBEX ? i18n('Kreditkarte') : i18n('SEPA Lastschrift-Mandat'));

  getBIC = (iban, bic) => {
    if (isAustrianIBAN(iban)) {
      return '';
    }
    return bic;
  };

  render() {
    const {
      info: {
        vacation,
        personal,
        coinsured,
        payment,
        promotion,
        visaRequired,
        acceptEmailSend,
        price,
        currency,
        brokerNumber
      },
      backLink,
      apiErrorMessages,
      loading,
      paymentPending
    } = this.props;

    let coinsuredClassName = '';

    if (paymentPending) {
      return (
        <div className="travel-confirmation loader-wrapper">
          <img src={loader} alt="loader" />
        </div>
      );
    }

    if (coinsured && coinsured.length > 1) {
      coinsuredClassName = 'coinsured-row';
    }

    vacation.startDate.set({
      hour: 0,
      minute: 0,
      second: 0,
      millisecond: 0
    });
    vacation.endDate.set({
      hour: 0,
      minute: 0,
      second: 0,
      millisecond: 0
    });

    let days = Math.ceil(moment.duration(vacation.endDate.diff(vacation.startDate)).asDays()) + 1;

    let bic = this.getBIC(payment.iban, payment.bic);

    return (
      <div className="page-step-wrapper page-step-wrapper--narrow travel-confirmation" id="confirmation-wrapper">
        {/* --- Insurance --- */}
        <Section header="Versicherung">
          <ConfirmationField title={i18n('Variante')} halfWidth={false} broadTitle={false}>
            <div>{`${insuranceName} - ${i18n('Reiseversicherung')}`}</div>
            <div>{this.getInsuranceType(vacation.type)}</div>
          </ConfirmationField>
          <ConfirmationField title={i18n('Destination')} halfWidth={false} broadTitle={false}>
            {this.getDestinationType(vacation.destination)}
          </ConfirmationField>
          <ConfirmationField title={i18n('Reisezeitraum')} halfWidth={false} broadTitle={false}>
            {this.getDateString()}
          </ConfirmationField>
          <ConfirmationField title={i18n('Tage')} halfWidth={false} broadTitle={false}>
            {days}
          </ConfirmationField>
          <ConfirmationField title={i18n('Stornoschutz')} halfWidth={false} broadTitle={false}>
            {vacation.cancellation ? 'Ja' : 'Nein'}
            {vacation.cancellation && vacation.cancellationAddDays && (
              <span className="cancelation-message">{`${'Stornoschutz ab'} ${moment()
                .add(10, 'days')
                .format('DD.MM.YYYY')}`}</span>
            )}
          </ConfirmationField>
          {vacation.cancellation && (
            <ConfirmationField title={i18n('Buchungsdatum')} halfWidth={false} broadTitle={false}>
              {moment(vacation.bookingDate).format('DD.MM.YYYY')}
            </ConfirmationField>
          )}
        </Section>

        {/* --- Personal Data --- */}
        <Section header="Versicherte Person">
          <ConfirmationField title={i18n('Name')} halfWidth={false} broadTitle={false}>
            {this.getNameString()}
          </ConfirmationField>
          <ConfirmationField title={i18n('Geburtsdatum')} halfWidth={false} broadTitle={false}>
            {moment(personal.geburtsdatum).format('DD.MM.YYYY')}
          </ConfirmationField>
          <ConfirmationField title={i18n('Telefon')} halfWidth={false} broadTitle={false}>
            {personal.telefon}
          </ConfirmationField>
          <ConfirmationField title={i18n('E-Mail')} halfWidth={false} broadTitle={false}>
            {personal.email}
          </ConfirmationField>
        </Section>

        <Section header={i18n('Wohnadresse')}>
          <ConfirmationField title={null}>
            <div>{`${personal.strasse} ${personal.nr}`}</div>
            <div>{personal.plz}</div>
            <div>{personal.ort}</div>
            <div>{countries[personal.land]}</div>
          </ConfirmationField>
        </Section>
        {/* --- Coinsured Persons Data --- */}
        {vacation.type === TRAVEL_FILTERS.FAMILY && (
          <Section header="Mitversicherte Person/en">
            {coinsured.map(
              (person, i) =>
                person.vorname !== '' && (
                  <div key={`coinsured${i}`}>
                    <ConfirmationField title={i18n('Name')} broadTitle={false}>
                      {`${person.anrede ? 'Frau' : 'Herr'} ${person.vorname} ${person.nachname}`}
                    </ConfirmationField>
                    <ConfirmationField title={i18n('Geburtsdatum')} broadTitle={false} className={coinsuredClassName}>
                      {moment.isMoment(person.geburtsdatum)
                        ? person.geburtsdatum.format('DD.MM.YYYY')
                        : moment(person.geburtsdatum).format('DD.MM.YYYY')}
                    </ConfirmationField>
                  </div>
                )
            )}
          </Section>
        )}

        {/* --- Miscellaneous --- */}
        {brokerNumber && (
          <Section header={i18n('Sonstiges')}>
            <ConfirmationField title={i18n('Vermittler-Nummer')}>{brokerNumber}</ConfirmationField>
          </Section>
        )}

        {/* --- Payment Information --- */}
        <Section header={i18n('Bezahlung')}>
          <ConfirmationField title={i18n('Zahlweise')} halfWidth={false} broadTitle={false}>
            {this.getPaymentType(payment.type)}
          </ConfirmationField>
          {payment.type === PAYMENT.SEPA && (
            <div>
              <ConfirmationField title={i18n('IBAN')} halfWidth={false} broadTitle={false}>
                {anonymise(payment.iban, 2, 4)}
              </ConfirmationField>
              {bic && (
                <ConfirmationField title={i18n('BIC')} halfWidth={false} broadTitle={false}>
                  {bic}
                </ConfirmationField>
              )}
            </div>
          )}
        </Section>
        <Section header={i18n('Vertragsabschluss')}>
          <ConfirmationField title={i18n('Kontaktaufnahme zu Werbezwecken')} halfWidth={false} broadTitle={false}>
            {promotion ? i18n('Ja') : i18n('Nein')}
          </ConfirmationField>
          <ConfirmationField
            title={i18n('Versicherungsbestätigung für Visumsantrag')}
            halfWidth={false}
            broadTitle={false}
          >
            {visaRequired ? i18n('Ja') : i18n('Nein')}
          </ConfirmationField>
        </Section>
        <Section header={i18n('Polizzenversand')}>
          <ConfirmationField title={i18n('Versand per E-Mail')} halfWidth={false} broadTitle={false}>
            {acceptEmailSend ? i18n('Ja') : i18n('Nein')}
          </ConfirmationField>
          {apiErrorMessages.length > 0 &&
            apiErrorMessages.map((error, index) => (
              <span key={`error-${index}`} className="error-message col-xs-12">
                <span className="error-icon" />
                {error.validierungsText}
              </span>
            ))}
        </Section>

        <div className="sticky-footer">
          <div className="page-step-wrapper__price">
            {i18n('Prämie (einmalig)')}&nbsp;
            {formatPrice(price, currency)}
          </div>
          <NavigationButtons
            backLink={backLink}
            nextText={i18n('Online abschließen')}
            loading={loading}
            canContinue={!loading}
            disabled={payment.type === PAYMENT.HOBEX ? this.state.blockConfirmClick : loading}
            onClick={this.onClick}
          />
        </div>
      </div>
    );
  }
}

Confirmation.propTypes = propTypes;
Confirmation.defaultProps = defaultProps;

export default Confirmation;
